import { createContext, useContext, useEffect, useState } from 'react';

// Theme types shared with the rest of the app
import type { ThemeContextType, ThemeProviderProps, Theme } from '../../types/app';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Consumer hook — throws if called outside ThemeProvider to avoid silent bugs
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children, defaultTheme = 'light' }: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Initialize from saved preference (if any) or fallback to default
    const saved = localStorage.getItem('astroshift-theme');
    return (saved as Theme) || defaultTheme;
  });

  // Apply theme to <html> via CSS classes and update CSS custom properties.
  // This keeps Tailwind utility classes and custom CSS in sync with the active theme.
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('theme-dark', 'theme-light', 'theme-lcars');
    
    // Add current theme class
    root.classList.add(`theme-${theme}`);
    
    // Update CSS custom properties based on theme
    switch (theme) {
      case 'dark':
        root.style.setProperty('--bg-primary', '#111827');
        root.style.setProperty('--bg-secondary', '#1f2937');
        root.style.setProperty('--text-primary', '#f9fafb');
        root.style.setProperty('--text-secondary', '#d1d5db');
        root.style.setProperty('--accent-primary', '#3b82f6');
        root.style.setProperty('--accent-secondary', '#10b981');
        root.style.setProperty('--status-bar-color', '#111827');
        root.style.setProperty('--navigation-bar-color', '#111827');
        break;
      case 'light':
        root.style.setProperty('--bg-primary', '#ffffff');
        root.style.setProperty('--bg-secondary', '#f9fafb');
        root.style.setProperty('--text-primary', '#111827');
        root.style.setProperty('--text-secondary', '#6b7280');
        root.style.setProperty('--accent-primary', '#3b82f6');
        root.style.setProperty('--accent-secondary', '#10b981');
        root.style.setProperty('--status-bar-color', '#ffffff');
        root.style.setProperty('--navigation-bar-color', '#ffffff');
        break;
      case 'lcars':
        root.style.setProperty('--bg-primary', '#000000');
        root.style.setProperty('--bg-secondary', '#1a1a2e');
        root.style.setProperty('--text-primary', '#ff9f40');
        root.style.setProperty('--text-secondary', '#40c9ff');
        root.style.setProperty('--accent-primary', '#ff6b6b');
        root.style.setProperty('--accent-secondary', '#4ecdc4');
        root.style.setProperty('--status-bar-color', '#000000');
        root.style.setProperty('--navigation-bar-color', '#ff9f40');
        break;
    }
    
    // Persist choice for next load
    localStorage.setItem('astroshift-theme', theme);
  }, [theme]);

  // Cycle between available themes in a predictable order
  const toggleTheme = () => {
    const themes: Theme[] = ['dark', 'light', 'lcars'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
